import { Avatar, Dropdown, Layout, Typography, Menu } from 'antd'
import {
  LogoutOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  TeamOutlined,
  NotificationOutlined,
  FormOutlined,
  DollarOutlined,
  StopOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import {
  isSysAdmin,
  isWorkspaceAdmin,
  hasBillingAccess,
  canViewTerminations,
  canAccessRequests,
  canViewWorkspaces
} from '../utils/roles'
import type { MenuProps } from 'antd'

const { Header } = Layout
const { Text } = Typography

export function AppHeader() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
      sessionStorage.clear()
      window.location.href = '/'
    }
  }

  const getUserDisplayName = () => {
    if (user?.profile.given_name) {
      return `${user.profile.given_name}`
    }
    return user?.profile.name || user?.profile.email || 'User'
  }

  const getUserEmail = () => {
    return user?.profile.email || ''
  }

  const getUserInitials = () => {
    const name = getUserDisplayName()
    if (!name || typeof name !== 'string') {
      return 'U'
    }
    const parts = name.split(' ')
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'name',
      type: 'group',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{getUserDisplayName()}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {getUserEmail()}
          </Text>
        </div>
      )
    },
    { key: 'divider', type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout
    }
  ]

  const navigationMenuItems: MenuProps['items'] = [
    ...(canAccessRequests(user)
      ? [
          {
            key: '/requests',
            icon: <FileTextOutlined />,
            label: 'Requests'
          }
        ]
      : []),
    ...(canViewWorkspaces(user)
      ? [
          {
            key: '/workspaces',
            icon: <AppstoreOutlined />,
            label: 'Workspaces'
          }
        ]
      : [])
  ]

  if (user && canViewTerminations(user)) {
    navigationMenuItems.push({
      key: '/terminations',
      icon: <StopOutlined />,
      label: 'Terminations'
    })
  }

  if (user && (isSysAdmin(user) || isWorkspaceAdmin(user))) {
    navigationMenuItems.push({
      key: '/announcements',
      icon: <NotificationOutlined />,
      label: 'Announcements'
    })
  }

  if (user && (isSysAdmin(user) || isWorkspaceAdmin(user))) {
    navigationMenuItems.push({
      key: '/surveys',
      icon: <FormOutlined />,
      label: 'Surveys'
    })
  }

  if (user && hasBillingAccess(user)) {
    navigationMenuItems.push({
      key: '/billing',
      icon: <DollarOutlined />,
      label: 'Billing'
    })
  }

  if (user && isSysAdmin(user)) {
    navigationMenuItems.push({
      key: '/internal-access',
      icon: <TeamOutlined />,
      label: 'Internal access'
    })
  }

  const getActiveKey = () => {
    if (location.pathname === '/workspaces' || location.pathname.startsWith('/workspaces/')) return '/workspaces'
    if (location.pathname === '/terminations') return '/terminations'
    if (location.pathname === '/announcements') return '/announcements'
    if (location.pathname === '/surveys') return '/surveys'
    if (location.pathname === '/billing') return '/billing'
    if (location.pathname === '/internal-access') return '/internal-access'
    return '/requests'
  }

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key)
  }

  return (
    <Header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 24px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        height: '48px',
        lineHeight: '48px',
        zIndex: 10
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '32px', flex: 1, minWidth: 0 }}>
        <Text strong style={{ fontSize: '18px', margin: 0, whiteSpace: 'nowrap' }}>
          Workspace management
        </Text>
        <Menu
          mode="horizontal"
          selectedKeys={[getActiveKey()]}
          items={navigationMenuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            backgroundColor: 'transparent',
            lineHeight: '48px',
            flex: 1
          }}
        />
      </div>

      <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <Avatar style={{ backgroundColor: '#1890ff' }} size={32}>
            {getUserInitials()}
          </Avatar>
        </div>
      </Dropdown>
    </Header>
  )
}
